import { useState } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekYear from 'dayjs/plugin/weekYear';
import { useTranslation } from 'react-i18next';
import Heading from "../../../component/Heading/Heading";
import UmsatzplanungTab from './UmsatzplanungTab';
import Umsatzplanung from './Umsatzplanung';
import Kostenplanung from './Kostenplanung';
import { Box, Divider, Typography } from '@mui/material';
import { formatAmount } from '../../../helper/formatAmount';
import { useCommonContext } from '../../../context/common';
import SelectCustomerPage from '../select-customer/SelectCustomerPage';
import "../../../styles/pages/customer/umsatxplanung.scss";
dayjs.extend(isoWeek);
dayjs.extend(weekYear);

const Umsatxplanung = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(dayjs().startOf('isoWeek').subtract(1, 'week')); // Start of current ISO week (Monday)
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState({
    weekStart: dayjs().startOf('isoWeek').subtract(1, 'week'), // Previous week start
    weekEnd: dayjs().endOf('isoWeek').subtract(1, 'week'),    // Previous week end
    calendarMonth: dayjs().subtract(1, 'week').startOf('month'),
    weekNumber: dayjs().subtract(1, 'week').isoWeek(),  // Previous week number
    year: dayjs().subtract(1, 'week').isoWeekYear()     // Previous week year
  });
  const [totals, setTotals] = useState({
    kostenplanungNetto: 0,
    kostenplanungBrutto: 0,
    umsatzplanungNetto: 0,
    umsatzplanungBrutto: 0,
  });
  const { clientId } = useCommonContext();


  if (!clientId) {
    return <SelectCustomerPage />
  }
  // Generate 5 weeks from current base date
  const weeks = Array.from({ length: 5 }, (_, i) => {
    const weekStart = startDate.add(i, 'week').startOf('isoWeek');
    const weekEnd = weekStart.endOf('isoWeek');
    return {
      label: `KW ${weekStart.isoWeek().toString().padStart(2, '0')}: ${weekStart.format('DD.MM')} - ${weekEnd.format('DD.MM')}`,
      weekStart,
      weekEnd,
      weekNumber: weekStart.isoWeek(),
      month: weekStart.month() + 1, // month is 0-indexed in dayjs
      year: weekStart.isoWeekYear(),
    };
  });

  // Handle week (tab) change
  const handleTabChange = (event, newIndex) => {
    setSelectedTab(newIndex);

    const selectedWeek = weeks[newIndex];
    setSelectedWeek({
      weekStart: selectedWeek.weekStart,
      weekEnd: selectedWeek.weekEnd,
      calendarMonth: selectedWeek.weekStart.startOf('month'),
      weekNumber: selectedWeek.weekNumber,
      year: selectedWeek.year
    });
    // Use this object for API or state update
    const { weekNumber, month, year } = selectedWeek;
  };

  const slideLeft = () => {
    const newStartDate = startDate.subtract(1, 'week');
    setStartDate(newStartDate);
    setSelectedTab(0);
    const weekStart = newStartDate.startOf('isoWeek');
    setSelectedWeek({
      weekStart: weekStart,
      weekEnd: weekStart.endOf('isoWeek'),
      calendarMonth: weekStart.startOf('month'),
      weekNumber: weekStart.isoWeek(),
      year: weekStart.isoWeekYear()
    });
  };

  const slideRight = () => {
    const newStartDate = startDate.add(1, 'week');
    setStartDate(newStartDate);
    setSelectedTab(0);
    const weekStart = newStartDate.startOf('isoWeek');
    setSelectedWeek({
      weekStart: weekStart,
      weekEnd: weekStart.endOf('isoWeek'),
      calendarMonth: weekStart.startOf('month'),
      weekNumber: weekStart.isoWeek(),
      year: weekStart.isoWeekYear()
    });
  };

  // Function to disable dates that aren't the start or end of the selected week
  const shouldDisableDate = (date) => {
    return !(
      date.isSame(selectedWeek.weekStart, 'day') ||
      date.isSame(selectedWeek.weekEnd, 'day') ||
      (date.isAfter(selectedWeek.weekStart) && date.isBefore(selectedWeek.weekEnd))
    );
  };

  const combinedNetto = totals?.umsatzplanungNetto - totals?.kostenplanungNetto;
  const combinedBrutto = totals?.umsatzplanungBrutto - totals?.kostenplanungBrutto;

  return (
    <div className="umsatxplanung-wrapper">
      <div className="umsatxplanung-heading flex items-center justify-between">
        <Heading text={t('module_umasatzplanung')} className="umsatxplanung-heading" bgColor="linear-gradient(90deg, #0075a4 0%, #009bd3 100%)" />
      </div>
      <div className="umsatxplanung-border">
        <UmsatzplanungTab
          slideLeft={slideLeft}
          slideRight={slideRight}
          selectedTab={selectedTab}
          handleTabChange={handleTabChange}
          weeks={weeks}
        />
        <Umsatzplanung shouldDisableDate={shouldDisableDate} clientId={clientId} setTotals={setTotals} selectedWeek={selectedWeek} />
        <Kostenplanung shouldDisableDate={shouldDisableDate} clientId={clientId} setTotals={setTotals} selectedWeek={selectedWeek} />
        <Divider
          sx={{
            margin: "1.5rem 1rem"
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: "space-between",
            padding: "0 1rem"
          }}
        >
          <Typography variant='h6'>
            {t('forderungen_netto')} {t('umsatzplanung_ergebnis')}: {formatAmount(combinedNetto) + " €"}
          </Typography>
          <Typography variant='h6'>
            {t('forderungen_brutto')} {t('umsatzplanung_ergebnis')}: {formatAmount(combinedBrutto) + " €"}
          </Typography>
        </Box>
      </div>
    </div>
  );
};

export default Umsatxplanung;
